import React, { useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Download, Copy, ChevronDown, ChevronRight, Upload } from 'lucide-react';

const CHUNK = 4;
const MAX_ITEMS = 20;
const MAX_IMG = 4 * 1024 * 1024;

export function parseList(text) {
  return text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(line => {
    const [nombre, precio, ...resto] = line.split(/[|\t;]/).map(s => s.trim());
    const p = parseFloat((precio || '').replace(/[$,\s]/g, ''));
    return { nombre, precio: Number.isFinite(p) ? p : null, notas: resto.join(' | ') || (Number.isFinite(p) ? '' : precio || '') };
  }).filter(i => i.nombre);
}

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

export function resultsToCsv(rows) {
  const head = ['Producto', 'Puntuacion', 'Veredicto', 'Factores', 'Pros', 'Contras', 'Tips', 'Error'];
  const lines = rows.map(r => [
    r.nombre, r.puntuacion ?? '', r.veredicto ?? '',
    (r.factores || []).map(f => `${f.nombre}${f.puntos != null ? ` (${f.puntos})` : ''}`).join(' / '),
    (r.pros || []).join(' / '), (r.contras || []).join(' / '), (r.tips || []).join(' / '), r.error || '',
  ].map(csvCell).join(','));
  return [head.map(csvCell).join(','), ...lines].join('\n');
}

const scoreColor = (s) => (s >= 80 ? 'text-emerald-400' : s >= 60 ? 'text-blue-400' : s >= 40 ? 'text-amber-400' : 'text-red-400');

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ScannerTab({ marketResults, marketQuery }) {
  const [mode, setMode] = useState('one');
  const [nombre, setNombre] = useState('');
  const [precio, setPrecio] = useState('');
  const [notas, setNotas] = useState('');
  const [foto, setFoto] = useState(null);
  const [listText, setListText] = useState('');
  const [useContext, setUseContext] = useState(false);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [cost, setCost] = useState(0);
  const fileInput = useRef(null);

  const analisis = useMemo(() => {
    if (!useContext || !marketResults) return undefined;
    return {
      query: marketQuery, strategy: marketResults.strategy, demand: marketResults.demand,
      total_sold: marketResults.total_sold, analyzed_count: marketResults.analyzed_count,
    };
  }, [useContext, marketResults, marketQuery]);

  const onFoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_IMG) { toast.error('La foto pesa más de 4 MB'); e.target.value = ''; return; }
    setFoto({ name: f.name, data: await fileToBase64(f) });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setListText(await f.text());
    e.target.value = '';
  };

  const evaluate = async () => {
    let items;
    if (mode === 'one') {
      if (!nombre.trim()) return toast.error('Escribe el nombre del producto');
      const p = parseFloat(precio);
      items = [{ nombre: nombre.trim(), precio: Number.isFinite(p) ? p : null, notas: notas.trim() || null, imagen_base64: foto?.data || null }];
    } else {
      items = parseList(listText);
      if (!items.length) return toast.error('Pega al menos un producto');
      if (items.length > MAX_ITEMS) return toast.error(`Máximo ${MAX_ITEMS} productos por evaluación`);
    }
    if (analisis) items = items.map(i => ({ ...i, analisis }));
    setRows(items.map(i => ({ nombre: i.nombre, estado: 'pendiente' })));
    setExpanded({});
    setCost(0);
    setRunning(true);
    let total = 0;
    for (let start = 0; start < items.length; start += CHUNK) {
      const chunk = items.slice(start, start + CHUNK);
      setRows(prev => prev.map((r, idx) => (idx >= start && idx < start + chunk.length ? { ...r, estado: 'evaluando' } : r)));
      let results;
      try {
        const res = await api.post('/research/scanner', { items: chunk, mercado: 'MX' });
        results = res.data.resultados;
        total += res.data.costo_estimado_usd || 0;
        setCost(total);
      } catch (err) {
        const msg = err.response?.data?.detail || 'Error al evaluar';
        results = chunk.map(c => ({ nombre: c.nombre, error: typeof msg === 'string' ? msg : 'Error al evaluar' }));
      }
      setRows(prev => prev.map((r, idx) => (idx >= start && idx < start + chunk.length ? { ...results[idx - start], estado: results[idx - start].error ? 'error' : 'listo' } : r)));
    }
    setRunning(false);
    toast.success('Evaluación terminada');
  };

  const sorted = useMemo(() => rows.map((r, i) => ({ ...r, _i: i })).sort((a, b) => {
    const sa = a.puntuacion ?? -1; const sb = b.puntuacion ?? -1;
    return sb - sa || a._i - b._i;
  }), [rows]);
  const done = rows.filter(r => r.estado === 'listo' || r.estado === 'error').length;
  const finished = rows.length > 0 && !running;

  const copyCsv = async () => {
    try { await navigator.clipboard.writeText(resultsToCsv(sorted)); toast.success('CSV copiado'); } catch { toast.error('No se pudo copiar'); }
  };
  const downloadCsv = () => {
    const url = URL.createObjectURL(new Blob(['﻿' + resultsToCsv(sorted)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'escaner-productos.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const input = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500';
  return (
    <div className="space-y-6" data-testid="scanner-tab">
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6 space-y-4">
        <div className="flex gap-2">
          {[['one', 'Un producto'], ['list', 'Lista']].map(([id, label]) => (
            <button key={id} onClick={() => setMode(id)} className={`px-4 py-2 rounded-lg text-sm ${mode === id ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300'}`}>{label}</button>
          ))}
        </div>
        {mode === 'one' ? (
          <div className="grid md:grid-cols-2 gap-3">
            <input className={input} placeholder="Nombre del producto" value={nombre} onChange={e => setNombre(e.target.value)} />
            <input className={input} placeholder="Precio de venta (opcional)" inputMode="decimal" value={precio} onChange={e => setPrecio(e.target.value)} />
            <textarea className={`${input} md:col-span-2`} rows={2} placeholder="Notas (opcional)" value={notas} onChange={e => setNotas(e.target.value)} />
            <div className="md:col-span-2 flex items-center gap-3">
              <input ref={fileInput} type="file" accept="image/*" onChange={onFoto} className="hidden" />
              <button onClick={() => fileInput.current?.click()} className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800"><Upload size={14} />Foto (opcional)</button>
              {foto && <span className="text-xs text-slate-400">{foto.name} <button className="text-red-400 ml-2" onClick={() => setFoto(null)}>quitar</button></span>}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <textarea className={input} rows={8} placeholder={'Un producto por línea: nombre | precio | notas\nLámpara solar LED | 249 | jardín\nOrganizador de cables | 99'} value={listText} onChange={e => setListText(e.target.value)} />
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <label className="cursor-pointer text-blue-400">Cargar CSV/TXT<input type="file" accept=".csv,.txt,text/plain,text/csv" onChange={onFile} className="hidden" /></label>
              <span>{parseList(listText).length} productos (máx. {MAX_ITEMS})</span>
            </div>
          </div>
        )}
        {marketResults && (
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={useContext} onChange={e => setUseContext(e.target.checked)} />
            Usar el análisis de mercado en pantalla{marketQuery ? ` («${marketQuery}»)` : ''} como contexto
          </label>
        )}
        <div className="flex items-center gap-3">
          <button data-testid="scanner-evaluate" onClick={evaluate} disabled={running} className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {running ? `Evaluando ${done}/${rows.length}...` : 'Evaluar'}
          </button>
          <p className="text-xs text-slate-500">Sin análisis de mercado la IA evalúa solo con criterio cualitativo y no inventa cifras.</p>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Resultados {cost > 0 && <span className="text-xs text-slate-500 font-normal ml-2">costo aprox. US${cost.toFixed(4)}</span>}</h3>
            {finished && (
              <div className="flex gap-2">
                <button onClick={copyCsv} className="flex items-center gap-1 px-3 py-1.5 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800"><Copy size={14} />Copiar CSV</button>
                <button onClick={downloadCsv} className="flex items-center gap-1 px-3 py-1.5 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800"><Download size={14} />Descargar CSV</button>
              </div>
            )}
          </div>
          <table className="w-full text-sm text-slate-300">
            <thead><tr className="text-left text-slate-500"><th className="p-2 w-8"></th><th className="p-2">Producto</th><th className="p-2">Puntuación</th><th className="p-2">Veredicto</th></tr></thead>
            <tbody>
              {sorted.map(r => {
                const open = !!expanded[r._i];
                const ok = r.puntuacion != null;
                return (
                  <React.Fragment key={r._i}>
                    <tr className={`border-t border-slate-800 ${ok ? 'cursor-pointer hover:bg-slate-800/40' : ''}`} onClick={() => ok && setExpanded(p => ({ ...p, [r._i]: !p[r._i] }))}>
                      <td className="p-2">{ok && (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}</td>
                      <td className="p-2 text-white">{r.nombre}</td>
                      <td className={`p-2 font-bold ${ok ? scoreColor(r.puntuacion) : ''}`}>{ok ? r.puntuacion : r.estado === 'error' ? '—' : r.estado === 'evaluando' ? 'Evaluando...' : 'En espera'}</td>
                      <td className="p-2">{r.error ? <span className="text-red-400">{r.error}</span> : r.veredicto}</td>
                    </tr>
                    {open && ok && (
                      <tr className="bg-slate-800/30"><td></td><td colSpan={3} className="p-3 space-y-3">
                        {!r.con_datos_mercado && <p className="text-xs text-amber-400">Sin datos de mercado: evaluación cualitativa.</p>}
                        <div>{(r.factores || []).map(f => <p key={f.nombre}><span className="text-white">{f.nombre}</span>{f.puntos != null && <span className="text-slate-500"> ({f.puntos})</span>}{f.comentario && <span> — {f.comentario}</span>}</p>)}</div>
                        <div className="grid md:grid-cols-3 gap-3">
                          {[['Pros', r.pros, 'text-emerald-400'], ['Contras', r.contras, 'text-red-400'], ['Tips', r.tips, 'text-blue-400']].map(([t, list, c]) => (
                            <div key={t}><p className={`text-xs font-semibold ${c} mb-1`}>{t}</p><ul className="list-disc ml-4 space-y-1">{(list || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                          ))}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
